<script setup lang="ts">
import FillOpacity from "~/telestrations/components/T/select/FillOpacity.vue";
import { ShapeFillColors, ShapeStrokeColors, ShapeStrokeTypeOptions, ShapeTextSizeOptions } from "~/telestrations/constants/colors";
import { type Shape, ShapeType } from "~/telestrations/types/canvas";

const { t } = useI18n();
const canvasStore = useCanvasStore();
const shapesStore = useShapesStore();

const rootEl = useTemplateRef<HTMLDivElement | null>("rootEl");
const { width: rootElWidth, height: rootElHeight } = useElementSize(rootEl);

const isStrokeSelectAvailable = computed(() => {
  if (!shapesStore.selectedShape) {
    return false;
  }

  return ShapeStrokeColors[shapesStore.selectedShape.type].length > 0;
});
const isFillSelectAvailable = computed(() => {
  if (!shapesStore.selectedShape) {
    return false;
  }

  return ShapeFillColors[shapesStore.selectedShape.type].length > 0;
});

const isFillOpacitySelectAvailable = computed(() => {
  if (!shapesStore.selectedShape) {
    return false;
  }
  return shapesStore.selectedShape.type === ShapeType.Rectangle || shapesStore.selectedShape.type === ShapeType.Ellipse;
});

const isBackgroundOpacitySelectAvailable = computed(() => {
  if (!shapesStore.selectedShape) {
    return false;
  }
  return shapesStore.selectedShape.type === ShapeType.Text;
});

const isBorderTypeSelectAvailable = computed(() => {
  if (!shapesStore.selectedShape) {
    return false;
  }

  return ShapeStrokeTypeOptions[shapesStore.selectedShape.type].length > 0;
});

const isThicknessSelectAvailable = computed(() => {
  if (!shapesStore.selectedShape) {
    return false;
  }

  return shapesStore.selectedShape.type === ShapeType.Line;
});
const isTextSizeSelectAvailable = computed(() => {
  if (!shapesStore.selectedShape) {
    return false;
  }

  return ShapeTextSizeOptions[shapesStore.selectedShape.type].length > 0;
});

const isSpotlightShapeSelectAvailable = computed(() => {
  if (!shapesStore.selectedShape) {
    return false;
  }

  return shapesStore.selectedShape.type === ShapeType.Spotlight;
});

const isBringToOptionAvailable = computed(() => {
  if (!shapesStore.selectedShape) {
    return false;
  }

  const ignoredTypes = [ShapeType.Spotlight, ShapeType.Text];
  return !ignoredTypes.includes(shapesStore.selectedShape.type);
});

const coords = computed(() => {
  return {
    x: shapesStore.transformerPosition?.x ?? 0,
    y: shapesStore.transformerPosition?.y ?? 0,
    scaleX: shapesStore.selectedShape?.scale?.x ?? 1,
    scaleY: shapesStore.selectedShape?.scale?.y ?? 1,
    height: shapesStore.transformerPosition?.height ?? 0,
  };
});

const transformerLeftPosition = computed(() => {
  if (!coords.value) {
    return 0;
  }

  const position = Math.round(coords.value.x * coords.value.scaleX);
  const maxXOffset = 16;
  const maxX = canvasStore.canvasSize.width - rootElWidth.value - maxXOffset;

  return Math.min(maxX, Math.max(0, position));
});
const transformerTopPosition = computed(() => {
  if (!coords.value) {
    return 0;
  }

  const topOffset = 60;

  const topPosition = Math.round(coords.value.y * coords.value.scaleY) - topOffset;
  const maxYOffset = 16;
  const maxY = canvasStore.canvasSize.height - rootElHeight.value - maxYOffset;

  if (topPosition <= 0) {
    const bottomPosition = coords.value.height + coords.value.y + maxYOffset;

    return bottomPosition > maxY ? 0 : bottomPosition;
  }

  return Math.min(maxY, Math.max(0, topPosition));
});

function onClickDelete(shape: Shape) {
  shapesStore.removeShape(shape.id);
}
</script>

<template>
  <div
    v-if="shapesStore.selectedShape && !shapesStore.isTransforming"
    ref="rootEl"
    class="absolute z-10 flex h-14 gap-x-2 rounded bg-white p-2 dark:bg-neutral-dark-700"
    :style="{
      left: `${transformerLeftPosition}px`,
      top: `${transformerTopPosition}px`,
    }"
  >
    <TSelectSpotlightShape v-if="isSpotlightShapeSelectAvailable" />
    <TSelectStroke v-if="isStrokeSelectAvailable" class="pl-0" />
    <UDivider v-if="isStrokeSelectAvailable" vertical lighter />
    <TSelectFill v-if="isFillSelectAvailable && !isFillOpacitySelectAvailable" />
    <UDivider v-if="isFillSelectAvailable && !isFillOpacitySelectAvailable" vertical lighter />
    <FillOpacity v-if="isFillOpacitySelectAvailable" type="fill" />
    <UDivider v-if="isFillOpacitySelectAvailable" vertical lighter />
    <TSelectThickness v-if="isThicknessSelectAvailable" />
    <UDivider v-if="isThicknessSelectAvailable" vertical lighter />
    <TSelectBorderType v-if="isBorderTypeSelectAvailable" />
    <UDivider v-if="isBorderTypeSelectAvailable" vertical lighter />
    <TSelectTextSize v-if="isTextSizeSelectAvailable" />
    <UDivider v-if="isTextSizeSelectAvailable" vertical lighter />
    <FillOpacity v-if="isBackgroundOpacitySelectAvailable" type="background" />
    <template v-if="isBringToOptionAvailable">
      <div class="flex items-center px-3.5 hover:bg-neutral-light-100 dark:hover:bg-neutral-dark-500">
        <TButton
          :icon="{ name: 'bring-front', class: 'size-5' }"
          variant="link-secondary"
          class="text-black hover:text-black dark:text-white dark:hover:text-white"
          :tooltip="t('labels.bring_to_front')"
          @click="shapesStore.sendShapeToTop(shapesStore.selectedShape.id)"
        />
      </div>
      <UDivider vertical lighter />
      <div class="flex items-center px-3.5 hover:bg-neutral-light-100 dark:hover:bg-neutral-dark-500">
        <TButton
          :icon="{ name: 'bring-back', class: 'size-5' }"
          variant="link-secondary"
          class="text-black hover:text-black dark:text-white dark:hover:text-white"
          :tooltip="t('labels.send_to_back')"
          @click="shapesStore.sendShapeToBottom(shapesStore.selectedShape.id)"
        />
      </div>
    </template>
    <UDivider vertical lighter />
    <div class="flex items-center px-3.5 hover:bg-neutral-light-100 dark:hover:bg-neutral-dark-500">
      <TButton
        :icon="{ name: 'delete', class: 'size-5' }"
        variant="link-danger"
        :tooltip="t('labels.delete')"
        @click="onClickDelete(shapesStore.selectedShape)"
      />
    </div>
  </div>
</template>
